import React, { useEffect, useState, useRef } from "react";
import { apiGet, apiPost, API_BASE_URL } from "../api";
import { useAuth } from "../AuthContext";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";

export default function Tickets() {
    const { me } = useAuth();
    const { ticketId } = useParams();
    const navigate = useNavigate();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTicket, setLoadingTicket] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [files, setFiles] = useState([]);
    const [newSubject, setNewSubject] = useState("");
    const [newText, setNewText] = useState("");
    const [sendingNew, setSendingNew] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [sendingReply, setSendingReply] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [toast, setToast] = useState(null);
    const [cat, setCat] = useState("");
    const [priority, setPriority] = useState("medium");
    const [status, setStatus] = useState("open");
    const messagesEndRef = useRef(null);

    const isAdmin = !!(me && me.roles && me.roles.includes("admin"));

    const showToast = m => {
        setToast(m);
        setTimeout(() => setToast(null), 1800);
    };

    const loadTickets = async () => {
        setLoading(true);
        try {
            const res = await apiGet("/tickets_list.php?ts=" + Date.now());
            if (res && res.ok && Array.isArray(res.tickets)) {
                setTickets(res.tickets);
            } else {
                setTickets([]);
            }
        } catch (e) {
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    const loadTicketDetail = async id => {
        if (!id) {
            setSelected(null);
            setMessages([]);
            setFiles([]);
            return;
        }
        setLoadingTicket(true);
        try {
            const res = await apiGet("/tickets_get.php?id=" + id + "&ts=" + Date.now());
            if (res && res.ok) {
                setSelected(res.ticket);
                setMessages(res.messages || []);
                setFiles(res.files || []);
                setCat(res.ticket.category || "");
                setPriority(res.ticket.priority || "medium");
                setStatus(res.ticket.status || "open");
            } else {
                setSelected(null);
                setMessages([]);
                setFiles([]);
                showToast("Tiket se nepodařilo načíst");
            }
        } catch (e) {
            setSelected(null);
            setMessages([]);
            setFiles([]);
            showToast("Tiket se nepodařilo načíst");
        } finally {
            setLoadingTicket(false);
            setTimeout(() => {
                if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
            }, 100);
        }
    };

    useEffect(() => {
        loadTickets();
    }, []);

    useEffect(() => {
        if (ticketId) {
            const idNum = parseInt(ticketId, 10);
            if (!Number.isNaN(idNum)) {
                setSelectedId(idNum);
            }
        } else {
            setSelectedId(null);
        }
    }, [ticketId]);

    useEffect(() => {
        if (selectedId) {
            loadTicketDetail(selectedId);
        } else {
            setSelected(null);
            setMessages([]);
            setFiles([]);
        }
    }, [selectedId]);

    const onSelectTicket = id => {
        setSelectedId(id);
        navigate("/tickets/" + id);
    };

    const onCreateTicket = async e => {
        e.preventDefault();
        if (!newSubject.trim() || !newText.trim()) {
            showToast("Vyplň předmět i text tiketu");
            return;
        }
        setSendingNew(true);
        try {
            const res = await apiPost("/tickets_create.php", {
                subject: newSubject.trim(),
                message: newText.trim()
            });
            if (!res || !res.ok || !res.id) {
                showToast("Vytvoření selhalo");
            } else {
                const id = res.id;
                setNewSubject("");
                setNewText("");
                showToast("Tiket vytvořen");
                await loadTickets();
                setSelectedId(id);
                navigate("/tickets/" + id);
                if (uploadFiles.length > 0) {
                    await uploadFilesForTicket(id);
                }
            }
        } catch (e) {
            showToast("Vytvoření selhalo");
        } finally {
            setSendingNew(false);
        }
    };

    const onSendReply = async e => {
        e.preventDefault();
        if (!selected || !replyText.trim()) return;
        setSendingReply(true);
        try {
            const res = await apiPost("/tickets_message.php", {
                ticket_id: selected.id,
                message: replyText.trim()
            });
            if (!res || !res.ok) {
                showToast("Odpověď selhala");
            } else {
                setReplyText("");
                await loadTicketDetail(selected.id);
            }
        } catch (e) {
            showToast("Odpověď selhala");
        } finally {
            setSendingReply(false);
        }
    };

    const uploadFilesForTicket = async ticketId => {
        if (!uploadFiles.length) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("ticket_id", ticketId);
            for (let i = 0; i < uploadFiles.length; i++) {
                fd.append("files[]", uploadFiles[i]);
            }
            const res = await fetch(API_BASE_URL + "/tickets_upload.php", {
                method: "POST",
                body: fd,
                credentials: "include"
            });
            const json = await res.json();
            if (!json.ok) {
                showToast("Nahrání souborů selhalo");
            } else {
                setUploadFiles([]);
                if (ticketId === (selected && selected.id)) {
                    await loadTicketDetail(ticketId);
                }
            }
        } catch (e) {
            showToast("Nahrání souborů selhalo");
        } finally {
            setUploading(false);
        }
    };

    const onAdminSave = async () => {
        if (!selected) return;
        try {
            const res = await apiPost("/tickets_update.php", {
                ticket_id: selected.id,
                category: cat,
                priority,
                status
            });
            if (res && res.ok) {
                showToast("Tiket upraven");
                await loadTickets();
                await loadTicketDetail(selected.id);
            } else {
                showToast("Uložení selhalo");
            }
        } catch (e) {
            showToast("Uložení selhalo");
        }
    };

    const onAdminClose = () => {
        if (!selected) return;
        setStatus("closed");
        onAdminSave();
    };

    const formatDate = s => {
        if (!s) return "";
        const d = new Date(s.replace(" ", "T"));
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleString();
    };

    const formatCategory = c => {
        if (!c) return "";
        const s = String(c);
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    };

    const labelFromPriority = p => {
        if (p === "low") return "Nízká";
        if (p === "high") return "Vysoká";
        if (p === "urgent") return "Urgentní";
        return "Střední";
    };

    const badgePriorityClass = p => {
        if (p === "low") return "bg-emerald-900/50 text-emerald-300 border-emerald-500/40";
        if (p === "high") return "bg-orange-900/50 text-orange-300 border-orange-500/40";
        if (p === "urgent") return "bg-red-900/60 text-red-300 border-red-500/50";
        return "bg-sky-900/50 text-sky-300 border-sky-500/40";
    };

    const badgeStatusClass = s => {
        if (s === "closed") return "bg-neutral-900 text-neutral-300 border-neutral-600";
        return "bg-lime-900/40 text-lime-300 border-lime-500/40";
    };

    const displayAuthor = msg => {
        const base = msg.username || "Uživatel";
        if (msg.is_admin === 1 || msg.is_admin === "1") return "Admin " + base;
        return base;
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-10">
            {toast && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
                    <div className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-sm backdrop-blur">
                        {toast}
                    </div>
                </div>
            )}

            <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-5xl font-extrabold mb-6 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
            >
                Podpora a tikety
            </motion.h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 shadow-lg shadow-black/40">
                        <div className="text-lg font-semibold mb-3">Nový tiket</div>
                        <form onSubmit={onCreateTicket} className="space-y-3">
                            <div>
                                <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                                    Předmět
                                </label>
                                <input
                                    type="text"
                                    value={newSubject}
                                    onChange={e => setNewSubject(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/15 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                                    maxLength={200}
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                                    Popis problému
                                </label>
                                <textarea
                                    value={newText}
                                    onChange={e => setNewText(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/15 text-sm min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                                    Soubory k tiketu
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                                    className="w-full text-xs text-white/70"
                                />
                                {uploadFiles.length > 0 && (
                                    <div className="mt-1 text-[11px] text-white/60">
                                        Vybráno {uploadFiles.length} souborů, max 20 MB každý
                                    </div>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={sendingNew}
                                className="w-full mt-1 px-4 py-2 rounded-xl bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {sendingNew ? "Odesílám..." : "Vytvořit tiket"}
                            </button>
                        </form>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 max-h-[480px] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-semibold">Tikety</div>
                            <div className="text-[11px] text-white/50">
                                {loading ? "Načítám…" : tickets.length}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                            {loading && (
                                <div className="text-xs text-white/60">Načítám tikety…</div>
                            )}
                            {!loading && tickets.length === 0 && (
                                <div className="text-xs text-white/50">
                                    Zatím nemáš žádný tiket. Nahoře vytvoř nový.
                                </div>
                            )}
                            {!loading && tickets.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => onSelectTicket(t.id)}
                                    className={
                                        "w-full text-left rounded-xl px-3 py-2 mb-1 border transition " +
                                        (selectedId === t.id
                                            ? "bg-white/15 border-emerald-400/60"
                                            : "bg-black/40 border-white/10 hover:bg-white/10")
                                    }
                                >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-semibold truncate">
                                                #{t.id} {t.subject}
                                            </div>
                                            {t.status !== "closed" && t.category && (
                                                <span className="inline-block mt-0.5 px-2 py-0.5 rounded-full border text-[10px] border-purple-400/50 text-purple-200 bg-purple-900/40">{formatCategory(t.category)}</span>
                                            )}
                                        </div>
                                        <span
                                            className={
                                                "text-[10px] px-2 py-0.5 rounded-full border " +
                                                badgeStatusClass(t.status)
                                            }
                                        >
                                            {t.status === "closed" ? "Uzavřený" : "Otevřený"}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-[11px] text-white/60">
                                        <span className={"px-2 py-0.5 rounded-full border " + badgePriorityClass(t.priority)}>
                                            {labelFromPriority(t.priority)}
                                        </span>
                                        <span>{formatDate(t.last_message_at)}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    {!selected && (
                        <div className="h-full min-h-[340px] flex items-center justify-center border border-dashed border-white/20 rounded-2xl bg-white/5">
                            <div className="text-center px-6">
                                <div className="text-lg mb-2">Vyber tiket, který chceš zobrazit</div>
                                <div className="text-sm text-white/60">
                                    Nebo nahoře vytvoř nový a hned se ti tady otevře konverzace s adminy.
                                </div>
                            </div>
                        </div>
                    )}

                    {selected && (
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col h-full min-h-[380px]">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 border-b border-white/10 pb-3">
                                <div>
                                    <div className="text-xs text-white/50">Tiket #{selected.id}</div>
                                    <div className="text-xl font-semibold">{selected.subject}</div>
                                    <div className="text-[11px] text-white/60 mt-1">
                                        Vytvořil {selected.username} • {formatDate(selected.created_at)}
                                    </div>
                                    <div className="text-xs mt-4">
                                        {selected.hasWl === 1 ? <span className="px-2 py-1 rounded-full border border-green-600 text-green-400">Aktivní Whitelist</span> : <span className="px-2 py-1 rounded-full border border-red-600 text-red-400">Neaktivní Whitelist</span>}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="flex flex-wrap gap-2 justify-end">
                                        <span className={"px-2 py-1 rounded-full border text-[11px] " + badgeStatusClass(status)}>
                                            {status === "closed" ? "Uzavřený" : "Otevřený"}
                                        </span>
                                        <span className={"px-2 py-1 rounded-full border text-[11px] " + badgePriorityClass(priority)}>
                                            {labelFromPriority(priority)}
                                        </span>
                                        {selected.category && (
                                            <span className="px-2 py-1 rounded-full border text-[11px] border-purple-400/50 text-purple-200 bg-purple-900/40">
                                                {formatCategory(selected.category)}
                                            </span>
                                        )}
                                    </div>
                                    {isAdmin && (
                                        <div className="flex flex-wrap gap-2 justify-end text-[11px]">
                                            <select
                                                value={cat}
                                                onChange={e => setCat(e.target.value)}
                                                className="bg-black/60 border border-white/20 rounded-lg px-2 py-1"
                                            >
                                                <option value="">Volné</option>
                                                <option value="v řešení">V řešení</option>
                                            </select>
                                            <select
                                                value={priority}
                                                onChange={e => setPriority(e.target.value)}
                                                className="bg-black/60 border border-white/20 rounded-lg px-2 py-1"
                                            >
                                                <option value="low">Nízká priorita</option>
                                                <option value="medium">Střední priorita</option>
                                                <option value="high">Vysoká priorita</option>
                                                <option value="urgent">Urgentní priorita</option>
                                            </select>
                                            <select
                                                value={status}
                                                onChange={e => setStatus(e.target.value)}
                                                className="bg-black/60 border border-white/20 rounded-lg px-2 py-1"
                                            >
                                                <option value="open">Otevřený</option>
                                                <option value="closed">Uzavřený</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={onAdminSave}
                                                className="px-3 py-1 rounded-lg bg-white/15 border border-white/30 hover:bg-white/25"
                                            >
                                                Uložit
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 flex flex-col min-h-[200px]">
                                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                                    {loadingTicket && (
                                        <div className="text-xs text-white/60">Načítám konverzaci…</div>
                                    )}
                                    {!loadingTicket && messages.length === 0 && (
                                        <div className="text-xs text-white/60">
                                            Zatím žádné zprávy v tomto tiketu.
                                        </div>
                                    )}
                                    {messages.map(msg => {
                                        const mine = me && String(msg.user_id) === String(me.id);
                                        const adminMsg = msg.is_admin === 1 || msg.is_admin === "1";
                                        return (
                                            <div
                                                key={msg.id}
                                                className={
                                                    "flex " +
                                                    (mine ? "justify-end" : "justify-start")
                                                }
                                            >
                                                <div
                                                    className={
                                                        "max-w-[80%] rounded-2xl px-3 py-2 text-sm border " +
                                                        (mine
                                                            ? "bg-emerald-900/60 border-emerald-500/50"
                                                            : adminMsg
                                                                ? "bg-purple-900/60 border-purple-500/50"
                                                                : "bg-white/10 border-white/20")
                                                    }
                                                >
                                                    <div className="flex items_center justify-between gap-3 mb-1">
                                                        <span className="text-[11px] font-semibold text-white/80">
                                                            {displayAuthor(msg)}
                                                        </span>
                                                        <span className="text-[10px] text-white/50">
                                                            {formatDate(msg.created_at)}
                                                        </span>
                                                    </div>
                                                    <div className="whitespace-pre-wrap text-[13px] text-white/90">
                                                        {msg.message}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>

                                <div className="mt-4 border-t border-white/10 pt-3 space-y-3">
                                    <div>
                                        <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                                            Soubory u tiketu
                                        </label>
                                        {files.length === 0 && (
                                            <div className="text-[11px] text-white/50">
                                                Zatím žádné soubory.
                                            </div>
                                        )}
                                        {files.length > 0 && (
                                            <div className="flex flex-wrap gap-2 text-[11px]">
                                                {files.map(f => (
                                                    <a
                                                        key={f.id}
                                                        href={API_BASE_URL + "/uploads/tickets/" + f.stored_name}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="px-2 py-1 rounded-lg bg-black/60 border border-white/20 hover:bg-white/10"
                                                    >
                                                        {f.original_name} ({Math.round(f.size / 1024)} kB)
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                type="file"
                                                multiple
                                                onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                                                className="text-[11px] text-white/70"
                                            />
                                            <button
                                                type="button"
                                                disabled={uploading || !uploadFiles.length}
                                                onClick={() => uploadFilesForTicket(selected.id)}
                                                className="px-3 py-1 rounded-lg bg-white/15 border border-white/30 text-[11px] hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {uploading ? "Nahrávám…" : "Nahrát"}
                                            </button>
                                        </div>
                                    </div>

                                    {status === "closed" ? (
                                        <div className="text-xs text_white/60">
                                            Tento tiket je uzavřený. V případě nového problému vytvoř nový tiket.
                                        </div>
                                    ) : (
                                        <form onSubmit={onSendReply} className="space-y-2">
                                            <label className="block text-xs uppercase tracking-wide text-white/60 mb-1">
                                                Odpověď do tiketu
                                            </label>
                                            <textarea
                                                value={replyText}
                                                onChange={e => setReplyText(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/15 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                                            />
                                            <div className="flex justify-end">
                                                <button
                                                    type="submit"
                                                    disabled={sendingReply || !replyText.trim()}
                                                    className="px-4 py-2 rounded-xl bg-emerald-500 text-black text-sm font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {sendingReply ? "Odesílám…" : "Odeslat odpověď"}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
